import { Field as ChakraField } from '@chakra-ui/react';
import * as React from 'react';
import { ReactElement } from 'react';
import { getInputStyles } from '@/components/ui/inputStyles';

export interface FieldProps extends Omit<ChakraField.RootProps, 'label'> {
	label?: React.ReactNode;
	helperText?: React.ReactNode;
	errorText?: React.ReactNode;
	optionalText?: React.ReactNode;
	requiredSuffix?: React.ReactNode;
}

export const Field = React.forwardRef<HTMLDivElement, FieldProps>(function Field(props, ref) {
	const { label, children, helperText, errorText, optionalText, requiredSuffix, ...rest } = props;

	/*
	 * The child is assumed to BE the input, and is cloned with the field styling painted onto it.
	 *
	 * That holds for a bare <Input>, and for a wrapper whose own props already declare the same pill,
	 * where the injected styles are a duplicate nobody sees. It breaks for any other child: a
	 * Combobox.Root, a Popover, a Box around a composed control all get a full field drawn onto them,
	 * which reads as the field being wrapped twice. This has caught people more than once.
	 *
	 * The child's own props are spread after, so a child that is not an input unsets what it does not
	 * want: `border="none" bg="transparent" h="auto" px={0} borderRadius={0}`. See the invite field in
	 * SharePopover, which wraps its pill in a combobox and does exactly that.
	 */
	const styledChildren = React.isValidElement(children)
		? React.cloneElement(children as ReactElement<Record<string, unknown>>, {
				...getInputStyles((children as ReactElement<Record<string, unknown>>).props ?? {}),
				...((children as ReactElement<Record<string, unknown>>).props ?? {}),
			})
		: children;

	return (
		<ChakraField.Root ref={ref} {...rest}>
			{label && (
				<ChakraField.Label color="brand.700" pl={4}>
					{label}
					<ChakraField.RequiredIndicator fallback={optionalText} />
					{requiredSuffix}
				</ChakraField.Label>
			)}
			{styledChildren}
			{helperText && <ChakraField.HelperText pl={4}>{helperText}</ChakraField.HelperText>}
			{errorText && <ChakraField.ErrorText pl={4}>{errorText}</ChakraField.ErrorText>}
		</ChakraField.Root>
	);
});
